import { type ClassValue, clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * `clsx` plus a `tailwind-merge` that knows this app's tokens (`D141`, plan
 * `D14`). Every component that takes a `className` from outside runs it
 * through here, so a caller's class beats the component's own.
 *
 * ── IT MERGES, AND IT HAS TO (`T-122`) ─────────────────────────────────────
 * Two utilities in the same group both land in the class list, and which one
 * paints follows the order Tailwind emitted them in the stylesheet, not the
 * order they were written in. `cx` removes the loser, so the LAST class in a
 * conflicting group is the one that survives, by contract rather than by luck.
 *
 * ── WHY THE MERGER IS CONFIGURED ───────────────────────────────────────────
 * `tailwind-merge` ships with Tailwind's default scales, not with this app's.
 * An unknown value falls through to a catch-all, and the catch-all is COLOUR:
 * plain `twMerge` reads `shadow-card shadow-accent` as two shadow colours and
 * throws `shadow-card` away, and it would do the same to a size named
 * `text-13` beside `text-ink`. Nothing fails; the element just loses a
 * property. Each list below teaches the merger one of `index.css`'s
 * namespaces, so the names land in the right group.
 *
 * ── THE LISTS FOLLOW `index.css`, AND A TEST SAYS SO ───────────────────────
 * Every name declared in an `@theme` block of `apps/mobile/src/index.css`
 * (the blocks that produce utilities) must be in the matching list here;
 * `__tests__/cx.test.ts` reads the stylesheet and fails on a gap in either
 * direction. The `--color-*-light` twins live on `:root`, not in `@theme`,
 * create no utility, and are therefore not listed.
 *
 * `touch`/`touch-lg` spacing (D141's Expo-era 48/56 steps) is absent because
 * `index.css` defines no `--spacing-*` yet. The day it does, the test fails
 * until `SPACING_TOKENS` learns it.
 */

/** Every `--color-*` registered in an `@theme` block, prefix stripped. */
export const COLOR_TOKENS = [
  "canvas",
  "surface",
  "surface-2",
  "line",
  "line-strong",
  "ink",
  "ink-2",
  "ink-3",
  "accent",
  "accent-press",
  "accent-ink",
  "on-accent",
  "accent-soft",
  "accent-mid",
  "accent-hatch",
  "plate",
  "plate-ink",
  "on-inverse",
  "ink-surface",
  "thumb",
  "knob",
  "on-ink",
  "success",
  "success-ink",
  "success-soft",
  "danger",
  "danger-soft",
  "on-danger",
  "warning",
  "warning-surface",
  "cel-sun",
  "cel-moss",
  "cel-mint",
  "moment-cta",
  "on-moment-cta",
  "cel-tile-ring",
  "shade",
  "glint",
  // `D209`: the plate calculator's competition colours and their text.
  "disc-25",
  "disc-20",
  "disc-15",
  "disc-10",
  "disc-5",
  "disc-2-5",
  "disc-1-25",
  "on-disc",
  "disc-ink",
  // `@theme inline`: aliases and theme-independent mixes.
  "inverse",
  "cel-ember",
  "cel-ink",
  "cel-paper",
  "on-ink-2",
  "on-ink-3",
  "ink-line",
  "ink-raised",
  // `@theme static`: per-theme mixes.
  "scrim",
  "cel-backdrop",
  "wash",
  "inverse-line",
  "plate-edge",
  "ink-edge",
  "tick",
  "tick-major",
] as const;

/** `--text-*` sizes (line heights are part of the size, not a name). */
export const TEXT_TOKENS = [
  "xs",
  "sm",
  "base",
  "lg",
  "xl",
  "2xl",
  "3xl",
] as const;

/** `--radius-*`. */
export const RADIUS_TOKENS = ["sm", "md", "lg", "xl", "2xl", "3xl"] as const;

/** `--shadow-*`, which resolve through the per-theme `--elevation-*`. */
export const SHADOW_TOKENS = ["lift", "ember", "up", "card", "tile"] as const;

/** `--font-*` families. */
export const FONT_TOKENS = ["display", "ui"] as const;

/** `--spacing-*`. None today; see the header. */
export const SPACING_TOKENS = [] as const;

/**
 * Built once, at module scope: `extendTailwindMerge` compiles a lookup trie,
 * and `cx` runs on every render of every component. The merger keeps its own
 * LRU cache, so a repeated class string costs a map lookup.
 */
const merge = extendTailwindMerge({
  extend: {
    theme: {
      color: [...COLOR_TOKENS],
      text: [...TEXT_TOKENS],
      radius: [...RADIUS_TOKENS],
      shadow: [...SHADOW_TOKENS],
      font: [...FONT_TOKENS],
      spacing: [...SPACING_TOKENS],
    },
  },
});

/** Joins the truthy parts and merges them: the last class in a group wins. */
export function cx(...inputs: ClassValue[]): string {
  return merge(clsx(inputs));
}
